<template>
  <div class="waste-container-picker">
    <div class="d-flex flex-wrap align-start ga-2">
      <v-select
        class="flex-grow-1 waste-container-picker__select"
        :model-value="modelValue"
        :items="items"
        item-title="title"
        item-value="value"
        :label="$t(label)"
        :loading="loading"
        :clearable="!required"
        :hint="hint || $t('workshop.form.container_picker_hint')"
        persistent-hint
        variant="outlined"
        density="comfortable"
        :error-messages="errorMessages || loadError"
        :no-data-text="$t('waste.picker.none')"
        @update:model-value="select"
      >
        <template #item="{ props: itemProps, item }">
          <v-list-item v-bind="itemProps">
            <template #append>
              <v-chip
                v-if="item.raw.suggested"
                size="x-small"
                color="green"
                variant="tonal"
              >
                {{ $t("waste.picker.suggested") }}
              </v-chip>
            </template>
          </v-list-item>
        </template>
      </v-select>
      <v-btn
        variant="outlined"
        color="primary"
        prepend-icon="mdi-qrcode-scan"
        min-height="48"
        @click="$refs.scanner.open()"
      >
        {{ $t("waste.picker.scan") }}
      </v-btn>
    </div>
    <waste-scanner
      ref="scanner"
      :check="scanProblem"
      @scanned="select"
      @camera-unavailable="loadError = $t('waste.picker.camera_unavailable')"
    />
  </div>
</template>

<script>
// Waste container picker of a depollution result (Stage 6 slice 2, plan §D): a select of
// GET /waste/containers/pick for the task (suggested containers first, "Fusto olio 3 · litri ·
// 13 02 05*"), in the result's unit, plus "Scansiona" (the label QR is the container id, D3).
// v-model is the containerId; "picked" emits the row ({ id, name, unit, ... }) or null, so the
// form can check the unit before saving. The API checks everything again.
import { pickWasteContainers } from "@/api/compliance";
import WasteScanner from "./wasteScanner.vue";
import { pickItems, pickedRow, scanMatch } from "./containerPick.mjs";

export default {
  components: { WasteScanner },
  props: {
    modelValue: { type: String, default: null },
    taskCode: { type: String, default: "" },
    // The result's unit: only containers in it are offered.
    unit: { type: String, default: null },
    label: { type: String, default: "workshop.form.container_picker" },
    hint: { type: String, default: "" },
    required: { type: Boolean, default: false },
    errorMessages: { type: String, default: "" },
  },
  emits: ["update:modelValue", "picked"],
  data: () => ({
    containers: [],
    loading: false,
    loadError: "",
  }),
  computed: {
    items() {
      return pickItems(this.containers, {
        unit: this.unit,
        selectedId: this.modelValue,
      }).map((item) => ({
        ...item,
        title: `${item.name} · ${this.$t(item.unitKey)} · ${item.code}`,
      }));
    },
  },
  watch: {
    taskCode() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.loadError = "";
      try {
        const { containers } = await pickWasteContainers({
          taskCode: this.taskCode,
        });
        this.containers = containers || [];
        this.$emit("picked", pickedRow(this.containers, this.modelValue));
      } catch (err) {
        this.loadError = this.$t("waste.picker.load_error");
      } finally {
        this.loading = false;
      }
    },
    select(id) {
      const value = id || null;
      this.$emit("update:modelValue", value);
      this.$emit("picked", pickedRow(this.containers, value));
    },
    // null accepts the scan; an i18n key keeps the scanner open with the message.
    scanProblem(text) {
      const match = scanMatch(text, this.containers);
      if (match.error === "not_found") return "waste.scanner.not_found";
      if (match.error) return "waste.scanner.invalid_code";
      return null;
    },
  },
};
</script>
